/**
 * @file Firestore CRUD for active game sessions and offline-cache mirroring. Each `Game`
 * document captures the players, the resolved challenge list, the current turn index, the
 * owner uid, and the start timestamp. Read paths mirror to IndexedDB (`utils/offlineGame`)
 * so a session can survive transient connectivity loss — the player keeps progressing
 * locally and Firestore is reconciled when it returns.
 *
 * `useGameStore` is the canonical caller — UI code should never reach into these helpers
 * directly except through that store.
 */

import {
  addDoc,
  collection,
  doc,
  onSnapshot,
  getDoc,
  updateDoc,
  deleteDoc
} from 'firebase/firestore'

import { type Router } from 'vue-router'
import { db } from '@/main'
import { saveGameState, loadGameState } from '@/utils/offlineGame'
import type { Game } from '@/types/Types'

/**
 * Subscribes to a single game document and resolves with its first hydrated snapshot.
 * Behaviour on edge cases:
 *  - Document missing → routes to `not-found-game` and the promise never resolves.
 *  - Snapshot error (e.g. offline) → falls back to the IndexedDB-cached game; routes to `not-found-game` if no cache.
 *
 * Note: the underlying `onSnapshot` listener is intentionally left open after resolve —
 * this keeps the offline cache fresh as the document changes, but callers do not get
 * reactivity beyond the first emission. Use the Pinia store for reactive consumption.
 */
export function getGame(gameID: string, router: Router): Promise<Game> {
  return new Promise((resolve) => {
    onSnapshot(
      doc(db, 'games', gameID),
      async (doc) => {
        if (!doc.exists() || doc.data() === undefined) {
          router.push({ name: 'not-found-game' })
          return
        }
        const data = doc.data()
        const fetchedGame: Game = {
          id: doc.id,
          challenges: data.challenges,
          packs: data.packs || [],
          currentChallengeIndex: data.currentChallengeIndex,
          gameStartedDate: data.gameStartedDate,
          owner: data.owner,
          players: data.players
        }
        // Save to offline storage
        await saveGameState(fetchedGame)
        resolve(fetchedGame)
      },
      async (_error) => {
        // If offline or error, try to load from local storage
        const offlineGame = await loadGameState(gameID)
        if (offlineGame) {
          resolve(offlineGame)
        } else {
          router.push({ name: 'not-found-game' })
        }
      }
    )
  })
}

/** Creates a new game document and returns the Firestore-generated id. The id is then written back onto the user via `assignGameToUser`. */
export async function createGame(game: Game) {
  const gameRef = await addDoc(collection(db, 'games'), game)
  return gameRef.id
}

/**
 * Persists the mutable per-turn state (`currentChallengeIndex` + `players` scores) to
 * Firestore and mirrors to the offline cache. No-ops when `game.id` is missing.
 * Static fields (`challenges`, `packs`, `owner`, `gameStartedDate`) are deliberately
 * not re-written — they are write-once at game creation.
 */
export async function updateGame(game: Game) {
  if (!game?.id) return
  // Save to Firestore
  const gameRef = doc(db, 'games', game.id)
  await updateDoc(gameRef, {
    currentChallengeIndex: game.currentChallengeIndex,
    players: game.players
  })
  // Save to offline storage
  await saveGameState(game)
}

/**
 * End-of-game bookkeeping on the owner's user document: archives the finished game into
 * `pastGame`, clears `currentGameId`, and increments `gamesPlayed`. Must be called
 * BEFORE `deleteGame` so the snapshot still exists when this read runs.
 */
export async function removeGameFromOwner(game: Game) {
  const userRef = doc(db, 'users', game.owner)
  const userData = (await getDoc(userRef)).data()

  await updateDoc(userRef, {
    pastGame: game,
    currentGameId: '',
    gamesPlayed: userData?.gamesPlayed + 1 || 1
  })
}

/** Hard-deletes the game document. The Cloud Functions scheduled cleanup also removes stale games — this is the immediate user-triggered path. */
export async function deleteGame(gameId: string) {
  const gameRef = doc(db, 'games', gameId)
  await deleteDoc(gameRef)
}
